/**
 * Admin → AE / CSM / Ops Manager routing rules.
 *
 * Rules drive the HubSpot deal-won → customer provisioning flow. Each rule has
 * (industry, sizeBand, region) match criteria — NULL columns are wildcards —
 * plus an assignment (aeEmail, optional csmEmail, optional opsMgrEmail),
 * priority (lower = higher precedence), active, and notes.
 *
 * Matching semantics (mirrors src/lib/hubspot/ae-routing.ts):
 *   - NULL criteria are wildcards
 *   - non-null criteria must all match (case-insensitive for industry/region,
 *     exact for sizeBand)
 *   - specificity = 4·industry + 2·sizeBand + 1·region
 *   - highest specificity wins, tie-break by priority asc
 */

import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

interface RoutingRule {
  id: string;
  industry: string | null;
  sizeBand: string | null;
  region: string | null;
  priority: number;
  aeEmail: string;
  csmEmail: string | null;
  opsMgrEmail: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

interface RuleDraft {
  industry: string;
  sizeBand: string;
  region: string;
  priority: number;
  aeEmail: string;
  csmEmail: string;
  opsMgrEmail: string;
  active: boolean;
  notes: string;
}

const SIZE_BANDS = [
  'Micro',
  'Small',
  'Mid Market',
  'Large Market',
  'Enterprise',
] as const;

function blankDraft(): RuleDraft {
  return {
    industry: '',
    sizeBand: '',
    region: '',
    priority: 100,
    aeEmail: '',
    csmEmail: '',
    opsMgrEmail: '',
    active: true,
    notes: '',
  };
}

function ruleToDraft(r: RoutingRule): RuleDraft {
  return {
    industry: r.industry ?? '',
    sizeBand: r.sizeBand ?? '',
    region: r.region ?? '',
    priority: r.priority,
    aeEmail: r.aeEmail,
    csmEmail: r.csmEmail ?? '',
    opsMgrEmail: r.opsMgrEmail ?? '',
    active: r.active,
    notes: r.notes ?? '',
  };
}

function draftToBody(d: RuleDraft) {
  return {
    industry: d.industry.trim() ? d.industry.trim() : null,
    sizeBand: d.sizeBand ? d.sizeBand : null,
    region: d.region.trim() ? d.region.trim() : null,
    priority: Number.isFinite(d.priority) ? d.priority : 100,
    aeEmail: d.aeEmail.trim().toLowerCase(),
    csmEmail: d.csmEmail.trim() ? d.csmEmail.trim().toLowerCase() : null,
    opsMgrEmail: d.opsMgrEmail.trim()
      ? d.opsMgrEmail.trim().toLowerCase()
      : null,
    active: d.active,
    notes: d.notes.trim() ? d.notes.trim() : null,
  };
}

/* ─── Local resolver — same semantics as src/lib/hubspot/ae-routing.ts ──── */

interface PreviewInput {
  industry: string | null;
  sizeBand: string | null;
  region: string | null;
}

function specificityScore(r: {
  industry: string | null;
  sizeBand: string | null;
  region: string | null;
}): number {
  let s = 0;
  if (r.industry) s += 4;
  if (r.sizeBand) s += 2;
  if (r.region) s += 1;
  return s;
}

function resolveLocal(
  rules: RoutingRule[],
  input: PreviewInput,
): RoutingRule | null {
  const candidates = rules
    .filter((r) => r.active)
    .filter((r) => {
      if (
        r.industry &&
        input.industry &&
        r.industry.toLowerCase() !== input.industry.toLowerCase()
      )
        return false;
      if (r.industry && !input.industry) return false;
      if (r.sizeBand && r.sizeBand !== input.sizeBand) return false;
      if (r.sizeBand && !input.sizeBand) return false;
      if (
        r.region &&
        input.region &&
        r.region.toLowerCase() !== input.region.toLowerCase()
      )
        return false;
      if (r.region && !input.region) return false;
      return true;
    });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const sa = specificityScore(a);
    const sb = specificityScore(b);
    if (sa !== sb) return sb - sa;
    return a.priority - b.priority;
  });
  return candidates[0];
}

/* ─── Page ────────────────────────────────────────────────────────────── */

export default function AdminAeRoutingPage() {
  const { loading: meLoading, can } = useMe();
  const canView = can('view:ae_routing');
  const canManage = can('manage:ae_routing');

  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor state — either creating new ('new') or editing an existing rule id.
  const [editorMode, setEditorMode] = useState<'closed' | 'new' | string>(
    'closed',
  );
  const [draft, setDraft] = useState<RuleDraft>(blankDraft());
  const [saving, setSaving] = useState(false);

  // Match preview state.
  const [previewIndustry, setPreviewIndustry] = useState('');
  const [previewSizeBand, setPreviewSizeBand] = useState('');
  const [previewRegion, setPreviewRegion] = useState('');
  const [previewResult, setPreviewResult] = useState<
    RoutingRule | null | undefined
  >(undefined);

  const load = async () => {
    try {
      setLoading(true);
      const d = await apiFetch<{ rules: RoutingRule[] }>(
        '/api/admin/ae-routing',
      );
      setRules(d.rules);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!meLoading && canView) load();
  }, [meLoading, canView]);

  if (meLoading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <div className="loading-text">Loading…</div>
      </div>
    );
  }
  if (!canView) {
    return (
      <div className="page">
        <div className="banner banner-error">
          You don&rsquo;t have permission to view AE / CSM / Ops Manager routing
          rules.
        </div>
      </div>
    );
  }

  const openCreate = () => {
    setDraft(blankDraft());
    setEditorMode('new');
  };
  const openEdit = (r: RoutingRule) => {
    setDraft(ruleToDraft(r));
    setEditorMode(r.id);
  };
  const closeEditor = () => {
    setEditorMode('closed');
    setDraft(blankDraft());
  };

  const submitEditor = async () => {
    const body = draftToBody(draft);
    if (!body.aeEmail) {
      setError('AE email is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editorMode === 'new') {
        await apiFetch('/api/admin/ae-routing', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch(`/api/admin/ae-routing/${editorMode}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      }
      closeEditor();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r: RoutingRule) => {
    if (!canManage) return;
    const next = !r.active;
    const prev = rules;
    setRules(rules.map((x) => (x.id === r.id ? { ...x, active: next } : x)));
    try {
      await apiFetch(`/api/admin/ae-routing/${r.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: next }),
      });
    } catch (e) {
      setRules(prev);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const deleteRule = async (r: RoutingRule) => {
    if (!canManage) return;
    const summary = describeCriteria(r);
    if (
      !confirm(
        `Delete routing rule for ${summary}?\nAE: ${r.aeEmail}\nThis cannot be undone.`,
      )
    )
      return;
    try {
      await apiFetch(`/api/admin/ae-routing/${r.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const runPreview = () => {
    const input: PreviewInput = {
      industry: previewIndustry.trim() ? previewIndustry.trim() : null,
      sizeBand: previewSizeBand ? previewSizeBand : null,
      region: previewRegion.trim() ? previewRegion.trim() : null,
    };
    setPreviewResult(resolveLocal(rules, input));
  };

  return (
    <div className="page">
      <div className="page-header">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <h1 style={{ margin: 0 }}>AE / CSM / Ops Manager Routing</h1>
          <span
            title="These rules are seeded with example data — replace with real sales-ops assignments before relying on them."
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 9px',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--amber-bg, #FCEFCF)',
              color: 'var(--amber-strong, #8A5B0E)',
              border: '1px solid rgba(180,130,30,0.35)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <span aria-hidden>⚠️</span> Placeholder
          </span>
        </div>
        <p className="page-intro">
          Routing rules pick the AE / CSM / Ops Manager when a new customer is
          provisioned from HubSpot. Lower priority wins ties. NULL columns are
          wildcards.
        </p>
      </div>

      {/* Rules table */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Routing rules</span>
          {canManage && (
            <button
              className="btn btn-outline"
              style={{
                background: 'rgba(255,254,252,0.06)',
                borderColor: 'rgba(255,254,252,0.18)',
                color: 'var(--text-white)',
              }}
              onClick={editorMode === 'new' ? closeEditor : openCreate}
            >
              {editorMode === 'new' ? 'Cancel' : 'Add rule'}
            </button>
          )}
        </div>
        <div className="section-body">
          {error && (
            <div className="banner banner-error" style={{ marginBottom: 12 }}>
              {error}
            </div>
          )}

          {editorMode === 'new' && (
            <RuleEditor
              draft={draft}
              setDraft={setDraft}
              onSubmit={submitEditor}
              onCancel={closeEditor}
              saving={saving}
              mode="new"
            />
          )}

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Priority</th>
                  <th>Match criteria</th>
                  <th>AE</th>
                  <th>CSM</th>
                  <th>Ops Mgr</th>
                  <th style={{ width: 90 }}>Active</th>
                  <th>Updated</th>
                  <th style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && rules.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>Loading…</td>
                  </tr>
                )}
                {!loading && rules.length === 0 && (
                  <tr className="empty">
                    <td colSpan={8}>
                      No routing rules yet.{' '}
                      {canManage ? 'Click "Add rule" to create one.' : ''}
                    </td>
                  </tr>
                )}
                {rules.map((r) => {
                  const isEditing = editorMode === r.id;
                  if (isEditing) {
                    return (
                      <tr key={r.id}>
                        <td colSpan={8} style={{ padding: 0 }}>
                          <RuleEditor
                            draft={draft}
                            setDraft={setDraft}
                            onSubmit={submitEditor}
                            onCancel={closeEditor}
                            saving={saving}
                            mode="edit"
                          />
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                      <td
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                        }}
                      >
                        {r.priority}
                      </td>
                      <td>
                        <CriteriaCell rule={r} />
                      </td>
                      <td>
                        <AssigneeCell email={r.aeEmail} />
                      </td>
                      <td>
                        <AssigneeCell email={r.csmEmail} />
                      </td>
                      <td>
                        <AssigneeCell email={r.opsMgrEmail} />
                      </td>
                      <td>
                        <ActiveChip
                          active={r.active}
                          clickable={canManage}
                          onClick={() => toggleActive(r)}
                        />
                      </td>
                      <td
                        style={{
                          fontSize: 'var(--fs-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        <div>{fmtRelative(r.updatedAt)}</div>
                        {r.updatedBy && (
                          <div
                            style={{
                              fontSize: 'var(--fs-xs)',
                              color: 'var(--text-faint)',
                            }}
                          >
                            by {r.updatedBy}
                          </div>
                        )}
                      </td>
                      <td>
                        {canManage ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className="btn-ghost"
                              style={{ fontSize: 'var(--fs-xs)' }}
                              onClick={() => openEdit(r)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-ghost"
                              style={{
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--red, #B33A3A)',
                              }}
                              onClick={() => deleteRule(r)}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span
                            style={{
                              fontSize: 'var(--fs-xs)',
                              color: 'var(--text-faint)',
                            }}
                          >
                            read-only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Match preview */}
      <div className="section">
        <div className="section-header">
          <span className="section-label">Match preview</span>
        </div>
        <div className="section-body">
          <p className="page-intro" style={{ marginTop: 0 }}>
            Sanity-check rule changes before a HubSpot deal hits them. Enter
            sample customer attributes and see which rule would win.
          </p>
          <div
            className="form-grid"
            style={{ gap: 8, alignItems: 'flex-end', marginBottom: 14 }}
          >
            <div>
              <label className="field-label">Industry</label>
              <input
                value={previewIndustry}
                onChange={(e) => setPreviewIndustry(e.target.value)}
                placeholder="e.g. Technology"
                className="input"
              />
            </div>
            <div>
              <label className="field-label">Size band</label>
              <select
                value={previewSizeBand}
                onChange={(e) => setPreviewSizeBand(e.target.value)}
                className="select"
              >
                <option value="">— Any —</option>
                {SIZE_BANDS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Region</label>
              <input
                value={previewRegion}
                onChange={(e) => setPreviewRegion(e.target.value)}
                placeholder="e.g. West"
                className="input"
              />
            </div>
            <button className="btn btn-primary" onClick={runPreview}>
              Show match
            </button>
          </div>

          {previewResult === undefined ? (
            <div
              style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-faint)' }}
            >
              Enter inputs and click &ldquo;Show match&rdquo; to preview the
              resolved assignment.
            </div>
          ) : previewResult === null ? (
            <div className="banner banner-error" style={{ margin: 0 }}>
              No rule matched. The HubSpot deal-won handler would fall through
              with no assignment.
            </div>
          ) : (
            <div
              className="form-grid"
              style={{
                padding: 14,
                background:
                  'linear-gradient(180deg, var(--accent-light) 0%, hsl(var(--surface-background)) 100%)',
                border: '1px solid var(--accent-soft)',
                borderRadius: 'var(--radius-sm)',
                gap: 14,
              }}
            >
              <div>
                <div className="field-label">Matched rule</div>
                <div style={{ fontWeight: 600 }}>
                  Priority {previewResult.priority} · specificity{' '}
                  {specificityScore(previewResult)}
                </div>
                <div
                  style={{
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 2,
                  }}
                >
                  <CriteriaCell rule={previewResult} />
                </div>
              </div>
              <div>
                <div className="field-label">AE</div>
                <AssigneeCell email={previewResult.aeEmail} />
              </div>
              <div>
                <div className="field-label">CSM</div>
                <AssigneeCell email={previewResult.csmEmail} />
              </div>
              <div>
                <div className="field-label">Ops Mgr</div>
                <AssigneeCell email={previewResult.opsMgrEmail} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ──────────────────────────────────────────────────── */

function RuleEditor({
  draft,
  setDraft,
  onSubmit,
  onCancel,
  saving,
  mode,
}: {
  draft: RuleDraft;
  setDraft: (d: RuleDraft) => void;
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
  mode: 'new' | 'edit';
}) {
  return (
    <div
      className="form-grid"
      style={{
        gap: 10,
        padding: 14,
        background:
          'linear-gradient(180deg, var(--accent-light) 0%, hsl(var(--surface-background)) 100%)',
        border: '1px solid var(--accent-soft)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 14,
      }}
    >
      <div>
        <label className="field-label">Industry (blank = any)</label>
        <input
          value={draft.industry}
          onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
          placeholder="e.g. Technology"
          className="input"
        />
      </div>
      <div>
        <label className="field-label">Size band (blank = any)</label>
        <select
          value={draft.sizeBand}
          onChange={(e) => setDraft({ ...draft, sizeBand: e.target.value })}
          className="select"
        >
          <option value="">— Any —</option>
          {SIZE_BANDS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="field-label">Region (blank = any)</label>
        <input
          value={draft.region}
          onChange={(e) => setDraft({ ...draft, region: e.target.value })}
          placeholder="e.g. West"
          className="input"
        />
      </div>

      <div>
        <label className="field-label">Priority (lower wins ties)</label>
        <input
          type="number"
          value={draft.priority}
          onChange={(e) =>
            setDraft({ ...draft, priority: Number(e.target.value) })
          }
          className="input"
        />
      </div>
      <div>
        <label className="field-label">AE email *</label>
        <input
          value={draft.aeEmail}
          onChange={(e) => setDraft({ ...draft, aeEmail: e.target.value })}
          placeholder="ae@cbtravel.com"
          className="input"
          type="email"
        />
      </div>
      <div>
        <label
          className="field-label"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          />
          Active
        </label>
        <div
          style={{
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-faint)',
            marginTop: 4,
          }}
        >
          Inactive rules are skipped during routing.
        </div>
      </div>

      <div>
        <label className="field-label">CSM email (optional)</label>
        <input
          value={draft.csmEmail}
          onChange={(e) => setDraft({ ...draft, csmEmail: e.target.value })}
          placeholder="csm@cbtravel.com"
          className="input"
          type="email"
        />
      </div>
      <div>
        <label className="field-label">Ops Manager email (optional)</label>
        <input
          value={draft.opsMgrEmail}
          onChange={(e) => setDraft({ ...draft, opsMgrEmail: e.target.value })}
          placeholder="ops@cbtravel.com"
          className="input"
          type="email"
        />
      </div>
      <div />

      <div style={{ gridColumn: '1 / -1' }}>
        <label className="field-label">Notes (optional)</label>
        <textarea
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          placeholder="Context for why this rule exists, who owns it, exceptions, etc."
          className="textarea"
          rows={2}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>

      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <button
          className="btn btn-outline"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={saving || !draft.aeEmail.trim()}
        >
          {saving ? 'Saving…' : mode === 'new' ? 'Create rule' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

function CriteriaCell({ rule }: { rule: RoutingRule }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 'var(--fs-sm)',
      }}
    >
      <CriteriaLine label="Industry" value={rule.industry} />
      <CriteriaLine
        label="Size"
        value={rule.sizeBand}
        chip={rule.sizeBand ? <SizeBandChip band={rule.sizeBand} /> : null}
      />
      <CriteriaLine label="Region" value={rule.region} />
    </div>
  );
}

function CriteriaLine({
  label,
  value,
  chip,
}: {
  label: string;
  value: string | null;
  chip?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          minWidth: 50,
        }}
      >
        {label}
      </span>
      {chip ? (
        chip
      ) : value ? (
        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
          {value}
        </span>
      ) : (
        <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>
          — any
        </span>
      )}
    </div>
  );
}

function SizeBandChip({ band }: { band: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--blue-bg, #DDEEF6)',
        color: 'var(--blue, #0E7D9A)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      {band}
    </span>
  );
}

function ActiveChip({
  active,
  clickable,
  onClick,
}: {
  active: boolean;
  clickable: boolean;
  onClick: () => void;
}) {
  const style: React.CSSProperties = {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 'var(--radius-pill)',
    fontSize: 'var(--fs-xs)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    border: '1px solid',
    cursor: clickable ? 'pointer' : 'default',
    fontFamily: 'inherit',
    transition: 'all 140ms',
    background: active ? 'var(--green-bg, #DDF1E4)' : 'var(--bg-subtle)',
    color: active ? 'var(--green-strong, #137A4F)' : 'var(--text-muted)',
    borderColor: active ? 'rgba(19,122,79,0.35)' : 'var(--border)',
  };
  if (clickable) {
    return (
      <button onClick={onClick} style={style} title="Click to toggle">
        {active ? 'Active' : 'Inactive'}
      </button>
    );
  }
  return <span style={style}>{active ? 'Active' : 'Inactive'}</span>;
}

function AssigneeCell({ email }: { email: string | null }) {
  if (!email) {
    return (
      <span
        style={{
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-faint)',
          fontStyle: 'italic',
        }}
      >
        —
      </span>
    );
  }
  const name = nameFromEmail(email);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
      <span
        style={{
          fontWeight: 600,
          fontSize: 'var(--fs-base)',
          color: 'var(--text-primary)',
        }}
      >
        {name}
      </span>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
        {email}
      </span>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────────────── */

function describeCriteria(r: RoutingRule): string {
  const parts: string[] = [];
  if (r.industry) parts.push(`industry=${r.industry}`);
  if (r.sizeBand) parts.push(`size=${r.sizeBand}`);
  if (r.region) parts.push(`region=${r.region}`);
  return parts.length ? parts.join(', ') : 'catch-all';
}

function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days === 0) {
    const min = Math.floor((Date.now() - t) / 60_000);
    if (min < 60) return min < 1 ? 'just now' : `${min}m ago`;
    return `${Math.floor(min / 60)}h ago`;
  }
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
